import React, { useState, useEffect, useCallback } from 'react';
import { addLockerGroup, updateLocker as updateLockerAPI, deleteLocker as deleteLockerAPI } from '../../api/axios';
import { useTranslation } from 'react-i18next';

// Locker Component
export const Locker = ({ locker, onAdd, updateLocker, onDelete, isPositionFree, viewMode = false, editMode = false, onSaveLocker }) => {
  const { t } = useTranslation('global');
  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [localLocker, setLocalLocker] = useState(locker);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setLocalLocker(locker);
  }, [locker]);

  const handleSave = async () => {
    if (!localLocker.locker_serial?.trim()) {
      alert(t("lockers.serialRequired"));
      return;
    }
    if (!localLocker.locker_pin?.trim()) {
      alert(t("lockers.pinRequired"));
      return;
    }

    setIsSaving(true);
    try {
      await onSaveLocker(localLocker);
      setIsEditing(false);
      updateLocker(localLocker);
    } catch (error) {
      console.error('Failed to save locker:', error);
      alert(t("lockers.saveFailed"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setLocalLocker(locker);
    setIsEditing(false);
  };

  const buttonStyle = {
    padding: '6px 10px',
    backgroundColor: '#f8f9fa',
    border: '2px solid #e9ecef',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#495057',
    transition: 'all 0.2s ease',
    minWidth: '32px',
    minHeight: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const buttonHoverStyle = {
    ...buttonStyle,
    backgroundColor: '#e9ecef',
    borderColor: '#dee2e6',
    transform: 'scale(1.05)',
  };

  const inputStyle = {
    width: '100%',
    padding: '8px 10px',
    border: '2px solid #e9ecef',
    borderRadius: '6px',
    fontSize: '14px',
    marginBottom: '8px',
    outline: 'none',
    transition: 'border-color 0.2s ease',
  };

  const inputFocusStyle = {
    ...inputStyle,
    borderColor: '#007bff',
    boxShadow: '0 0 0 2px rgba(0, 123, 255, 0.25)',
  };

  const isInEditMode = editMode && isEditing;
  const isReadOnly = viewMode || (editMode && !isEditing);

  return (
    <div 
      style={{
        border: `2px solid ${isInEditMode ? '#007bff' : '#e9ecef'}`,
        padding: '16px',
        margin: '4px',
        width: '220px',
        backgroundColor: isInEditMode ? '#f8f9ff' : 'white',
        borderRadius: '12px',
        boxShadow: isHovered ? '0 4px 12px rgba(0,0,0,0.15)' : '0 2px 8px rgba(0,0,0,0.08)',
        position: 'relative',
        transition: 'all 0.2s ease',
        transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Action buttons */}
      <div style={{
        position: 'absolute',
        top: '8px',
        right: '8px',
        display: 'flex',
        gap: '4px',
        opacity: isHovered ? 1 : 0.7,
        transition: 'opacity 0.2s ease',
      }}>
        {editMode && !isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              cursor: 'pointer',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title={t("lockers.editLocker")}
          >
            ✎
          </button>
        )}
        
        {editMode && isEditing && (
          <>
            <button
              onClick={handleSave}
              disabled={isSaving}
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: isSaving ? '#6c757d' : '#28a745',
                color: 'white',
                border: 'none',
                cursor: isSaving ? 'not-allowed' : 'pointer',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title={t("lockers.saveLocker")}
            >
              {isSaving ? '⏳' : '✓'}
            </button>
            <button
              onClick={handleCancel}
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                cursor: 'pointer',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title={t("lockers.cancelEdit")}
            >
              ✕
            </button>
          </>
        )}

        {(editMode || !viewMode) && !isEditing && (
          <button
            onClick={() => onDelete(locker.id)}
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              cursor: 'pointer',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title={t("lockers.deleteLocker")}
          >
            ×
          </button>
        )}
      </div>

      {/* Top button - only show in create mode */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        marginBottom: '12px',
        height: '32px'
      }}>
        {!viewMode && !editMode && isPositionFree(locker.position_x, locker.position_y - 1) && (
          <button 
            onClick={() => onAdd('top')}
            style={isHovered ? buttonHoverStyle : buttonStyle}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#e9ecef'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#f8f9fa'}
            title={t("lockers.addAbove")}
          >
            ↑
          </button>
        )}
      </div>

      {/* Middle section with left button, inputs, and right button */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        minHeight: '80px',
        gap: '8px'
      }}>
        {/* Left button - only show in create mode */}
        <div style={{ width: '32px', paddingTop: '20px' }}>
          {!viewMode && !editMode && isPositionFree(locker.position_x - 1, locker.position_y) && (
            <button 
              onClick={() => onAdd('left')}
              style={isHovered ? buttonHoverStyle : buttonStyle}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#e9ecef'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#f8f9fa'}
              title={t("lockers.addLeft")}
            >
              ←
            </button>
          )}
        </div>
        
        {/* Input fields */}
        <div style={{ flex: 1 }}>
          <input
            value={isInEditMode ? localLocker.locker_serial : locker.locker_serial}
            onChange={isInEditMode ? (e) => setLocalLocker({ ...localLocker, locker_serial: e.target.value }) : 
                     !isReadOnly ? (e) => updateLocker({ ...locker, locker_serial: e.target.value }) : undefined}
            placeholder={isReadOnly ? t("lockers.noSerial") : t("lockers.enterSerial")}
            readOnly={isReadOnly}
            style={{
              ...inputStyle,
              backgroundColor: isReadOnly ? '#f8f9fa' : 'white',
              cursor: isReadOnly ? 'default' : 'text',
              borderColor: isInEditMode ? '#007bff' : '#e9ecef'
            }}
            onFocus={isReadOnly ? undefined : (e) => Object.assign(e.target.style, inputFocusStyle)}
            onBlur={isReadOnly ? undefined : (e) => Object.assign(e.target.style, inputStyle)}
          />
          <input
            value={isInEditMode ? localLocker.locker_pin : locker.locker_pin}
            onChange={isInEditMode ? (e) => setLocalLocker({ ...localLocker, locker_pin: e.target.value }) : 
                     !isReadOnly ? (e) => updateLocker({ ...locker, locker_pin: e.target.value }) : undefined}
            placeholder={isReadOnly ? t("lockers.noPin") : t("lockers.enterPin")}
            readOnly={isReadOnly}
            style={{
              ...inputStyle,
              backgroundColor: isReadOnly ? '#f8f9fa' : 'white',
              cursor: isReadOnly ? 'default' : 'text',
              borderColor: isInEditMode ? '#007bff' : '#e9ecef'
            }}
            onFocus={isReadOnly ? undefined : (e) => Object.assign(e.target.style, inputFocusStyle)}
            onBlur={isReadOnly ? undefined : (e) => Object.assign(e.target.style, inputStyle)}
          />
        </div>

        {/* Right button - only show in create mode */}
        <div style={{ width: '32px', paddingTop: '20px' }}>
          {!viewMode && !editMode && isPositionFree(locker.position_x + 1, locker.position_y) && (
            <button 
              onClick={() => onAdd('right')}
              style={isHovered ? buttonHoverStyle : buttonStyle}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#e9ecef'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#f8f9fa'}
              title={t("lockers.addRight")}
            >
              →
            </button>
          )}
        </div>
      </div>

      {/* Bottom button - only show in create mode */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        marginTop: '12px',
        height: '32px'
      }}>
        {!viewMode && !editMode && isPositionFree(locker.position_x, locker.position_y + 1) && (
          <button 
            onClick={() => onAdd('bottom')}
            style={isHovered ? buttonHoverStyle : buttonStyle}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#e9ecef'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#f8f9fa'}
            title={t("lockers.addBelow")}
          >
            ↓
          </button>
        )}
      </div>

      {/* Position indicator */}
      <div style={{
        position: 'absolute',
        bottom: '4px',
        left: '8px',
        fontSize: '10px',
        color: '#6c757d',
        fontFamily: 'monospace',
      }}>
        ({locker.position_x}, {locker.position_y})
      </div>
    </div>
  );
};

// Main LockerArrangement Component
const LockerArrangement = ({ 
  loadedLockers, 
  viewMode = false, 
  editMode = false, 
  groupName = '', 
  onLockerUpdate, 
  onLockerDelete,
  onGroupDelete 
}) => {
  const { t } = useTranslation('global');
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [showHeaders, setShowHeaders] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isGroupOperationLoading, setIsGroupOperationLoading] = useState(false);
  
  const [lockers, setLockers] = useState(
    loadedLockers && loadedLockers.length > 0 
      ? loadedLockers 
      : [{
          id: Date.now(),
          locker_serial: '',
          locker_pin: '',
          position_x: 0,
          position_y: 0,
          barrier_sn: '',
          locker_group: '',
          reader_serial_num: '',
          status: 0
        }]
  );

  const [headerValues, setHeaderValues] = useState({
    locker_group: '',
    barrier_sn: '',
    reader_serial_num: ''
  });

  // Update lockers when loadedLockers prop changes
  useEffect(() => {
    if (loadedLockers && loadedLockers.length > 0) {
      setLockers(loadedLockers);
      // Extract header values from first locker if available
      const firstLocker = loadedLockers[0];
      setHeaderValues({
        locker_group: firstLocker.locker_group || '',
        barrier_sn: firstLocker.barrier_sn || '',
        reader_serial_num: firstLocker.reader_serial_num || ''
      });
    }
  }, [loadedLockers]);

  // Check if position is free
  const isPositionFree = useCallback((x, y) => {
    return !lockers.some(l => l.position_x === x && l.position_y === y);
  }, [lockers]);

  // Update individual locker
  const updateLocker = useCallback((updatedLocker) => {
    setLockers(prev => prev.map(lock => 
      lock.id === updatedLocker.id ? updatedLocker : lock
    ));
  }, []);

  // Save individual locker
  const saveLocker = useCallback(async (locker) => {
    try {
      if (onLockerUpdate) {
        await onLockerUpdate(locker);
      } else {
        await updateLockerAPI(locker.id, locker);
      }
    } catch (error) {
      throw error;
    }
  }, [onLockerUpdate]);

  // Delete locker
  const deleteLocker = useCallback(async (lockerId) => {
    if (!editMode && lockers.length <= 1) {
      alert(t("lockers.cannotDeleteLast"));
      return;
    }

    const confirmed = window.confirm(t("lockers.confirmDeleteLocker"));
    if (!confirmed) return;

    try {
      if (editMode && onLockerDelete) {
        await onLockerDelete(lockerId);
      } else if (editMode) {
        await deleteLockerAPI(lockerId);
      }
      setLockers(prev => prev.filter(lock => lock.id !== lockerId));
    } catch (error) {
      console.error('Failed to delete locker:', error);
      alert(t("lockers.deleteFailed"));
    }
  }, [lockers.length, editMode, onLockerDelete, t]);

  // Delete entire group
  const deleteGroup = useCallback(async () => {
    const confirmed = window.confirm(t("lockers.confirmDeleteGroup", { groupName }));
    if (!confirmed) return;

    setIsGroupOperationLoading(true);
    try {
      if (onGroupDelete) {
        await onGroupDelete(groupName);
      } else {
        // Delete all lockers in the group
        for (const locker of lockers) {
          await deleteLockerAPI(locker.id);
        }
      }
      setLockers([]);
    } catch (error) {
      console.error('Failed to delete group:', error);
      alert(t("lockers.groupDeleteFailed"));
    } finally {
      setIsGroupOperationLoading(false);
    }
  }, [groupName, lockers, onGroupDelete, t]);

  // Save entire group
  const saveGroup = useCallback(async () => {
    setIsGroupOperationLoading(true);
    try {
      for (const locker of lockers) {
        if (onLockerUpdate) {
          await onLockerUpdate(locker);
        } else {
          await updateLockerAPI(locker.id, locker);
        }
      }
      alert(t("lockers.groupSaved"));
    } catch (error) {
      console.error('Failed to save group:', error);
      alert(t("lockers.groupSaveFailed"));
    } finally {
      setIsGroupOperationLoading(false);
    }
  }, [lockers, onLockerUpdate, t]);

  // Add new locker (only in create mode)
  const addLocker = useCallback((baseLocker, direction) => {
    let x = baseLocker.position_x;
    let y = baseLocker.position_y;

    switch(direction) {
      case 'top': y -= 1; break;
      case 'right': x += 1; break;
      case 'bottom': y += 1; break;
      case 'left': x -= 1; break;
      default: return;
    }

    if (!isPositionFree(x, y)) {
      return;
    }

    const newLocker = {
      id: Date.now() + Math.random(),
      locker_serial: '',
      locker_pin: '',
      position_x: x,
      position_y: y,
      barrier_sn: headerValues.barrier_sn || '',
      locker_group: headerValues.locker_group || '',
      reader_serial_num: headerValues.reader_serial_num || '',
      status: 0
    };

    setLockers(prev => [...prev, newLocker]);
  }, [headerValues, isPositionFree]);

  // Mouse event handlers for dragging
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart]);

  // Handle header input changes
  const handleHeaderChange = useCallback((e, field) => {
    const value = e.target.value;
    setHeaderValues(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Update all existing lockers with new header value
    setLockers(prev => prev.map(locker => ({
      ...locker,
      [field]: value
    })));
  }, []);

  // Save arrangement (create mode)
  const validateLockers = () => {
    for (const locker of lockers) {
      if (!locker.locker_serial?.trim()) {
        alert(t("lockers.missingSerial", { 
          x: locker.position_x, 
          y: locker.position_y 
        }));
        return false;
      }
      if (!locker.locker_pin?.trim()) {
        alert(t("lockers.missingPin", { 
          x: locker.position_x, 
          y: locker.position_y 
        }));
        return false;
      }
      if (!locker.locker_group?.trim()) {
        alert(t("lockers.missingGroup", { 
          x: locker.position_x, 
          y: locker.position_y 
        }));
        return false;
      }
      if (!locker.barrier_sn?.trim()) {
        alert(t("lockers.missingElectronicsId", { 
          x: locker.position_x, 
          y: locker.position_y 
        }));
        return false;
      }
    }
    return true;
  };

  const saveArrangement = async () => {
    if (!validateLockers()) return;

    setIsLoading(true);
    try {
      const lockerGroup = lockers.map(locker => ({
        id: locker.id,
        position_x: locker.position_x,
        position_y: locker.position_y,
        locker_serial: locker.locker_serial,
        locker_pin: locker.locker_pin,
        barrier_sn: locker.barrier_sn,
        locker_group: locker.locker_group,
        reader_serial_num: locker.reader_serial_num || '',
        locker_status: locker.status
      }));
      
      const response = await addLockerGroup(lockerGroup);
      console.log('Saved successfully:', response);
      alert(t("lockers.arrangementSaved"));
    } catch (error) {
      console.error('Save failed:', error);
      alert(t("lockers.arrangementSaveFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  // Reset view
  const resetView = useCallback(() => {
    setPosition({ x: 0, y: 0 });
    setZoom(1);
  }, []);

  // Input styles
  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    border: '2px solid #e9ecef',
    borderRadius: '6px',
    fontSize: '14px',
    marginBottom: '8px',
    outline: 'none',
    transition: 'border-color 0.2s ease',
  };

  return (
    <div style={{ 
      padding: '20px',
      minHeight: '100vh',
      position: 'relative',
      overflow: 'hidden',
      backgroundColor: '#f8f9fa'
    }}>
      {/* Header Panel - only show in create mode */}
      {!viewMode && !editMode && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 100,
          minWidth: '280px',
          maxWidth: '320px',
          display: showHeaders ? 'block' : 'none',
          border: '2px solid #e9ecef'
        }}>
          <h3 style={{ margin: '0 0 16px 0', color: '#495057', fontSize: '18px' }}>
            {t("lockers.configuration")}
          </h3>
          
          <div style={{ marginBottom: '16px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '6px', 
              fontWeight: '600',
              color: '#495057'
            }}>
              {t("lockers.lockerGroup")}
            </label>
            <input 
              type="text" 
              value={headerValues.locker_group}
              onChange={(e) => handleHeaderChange(e, 'locker_group')}
              style={inputStyle}
              placeholder={t("lockers.enterGroup")}
            />
          </div>
          
          <div style={{ marginBottom: '16px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '6px', 
              fontWeight: '600',
              color: '#495057'
            }}>
              {t("lockers.electronicsId")}
            </label>
            <input 
              type="text" 
              value={headerValues.barrier_sn}
              onChange={(e) => handleHeaderChange(e, 'barrier_sn')}
              style={inputStyle}
              placeholder={t("lockers.enterElectronicsId")}
            />
          </div>
          
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '6px', 
              fontWeight: '600',
              color: '#495057'
            }}>
              {t("lockers.readerId")}
            </label>
            <input 
              type="text" 
              value={headerValues.reader_serial_num}
              onChange={(e) => handleHeaderChange(e, 'reader_serial_num')}
              style={inputStyle}
              placeholder={t("lockers.enterReaderId")}
            />
          </div>

          <div style={{ 
            marginTop: '16px', 
            paddingTop: '16px', 
            borderTop: '1px solid #e9ecef',
            fontSize: '14px',
            color: '#6c757d'
          }}>
            {t("lockers.totalLockers")}: {lockers.length}
          </div>
        </div>
      )}

      {/* Toggle Header Button - only show in create mode */}
      {!viewMode && !editMode && (
        <button 
          onClick={() => setShowHeaders(!showHeaders)}
          style={{
            position: 'fixed',
            top: '20px',
            right: showHeaders ? '340px' : '20px',
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            backgroundColor: 'white',
            border: '2px solid #e9ecef',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 101,
            fontSize: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            transition: 'all 0.2s ease'
          }}
          title={showHeaders ? t("lockers.hideConfiguration") : t("lockers.showConfiguration")}
        >
          {showHeaders ? '×' : '⚙'}
        </button>
      )}

      {/* Status indicator */}
      <div style={{
        position: 'fixed',
        top: (viewMode || editMode) ? '100px' : '20px',
        left: '20px',
        backgroundColor: 'white',
        padding: '8px 12px',
        borderRadius: '6px',
        border: '2px solid #e9ecef',
        fontSize: '14px',
        color: '#495057',
        zIndex: 100
      }}>
        {(viewMode || editMode) && (
          <div style={{ marginBottom: '4px', fontWeight: '600' }}>
            {groupName}
            {editMode && (
              <span style={{ 
                marginLeft: '8px', 
                color: '#007bff', 
                fontSize: '12px' 
              }}>
                [{t("lockers.editMode")}]
              </span>
            )}
          </div>
        )}
        {t("lockers.zoom")}: {Math.round(zoom * 100)}%
      </div>

      {/* Main canvas area */}
      <div 
        style={{
          position: 'fixed',
          left: '50%',
          top: '50%',
          width: '240px',
          height: '240px',
          transform: `translate(-50%, -50%) translate(${position.x}px, ${position.y}px)`,
          cursor: isDragging ? 'grabbing' : 'grab',
          transition: isDragging ? 'none' : 'transform 0.1s ease-out',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none'
        }}
        onMouseDown={(e) => {
          if (e.target === e.currentTarget || e.target.closest('[data-draggable]')) {
            setIsDragging(true);
            setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
          }
        }}
        onWheel={(e) => {
          e.preventDefault();
          const zoomDelta = e.deltaY > 0 ? -0.1 : 0.1;
          setZoom(prev => Math.max(0.3, Math.min(3, prev + zoomDelta)));
        }}
        data-draggable
      >
        <div style={{
          transform: `scale(${zoom})`,
          transformOrigin: 'center center'
        }}>
          {lockers.map(locker => (
            <div 
              key={locker.id}
              style={{
                position: 'absolute',
                left: `${locker.position_x * 240}px`,
                top: `${locker.position_y * 240}px`
              }}
            >
              <Locker
                locker={locker}
                onAdd={(dir) => addLocker(locker, dir)}
                updateLocker={updateLocker}
                onDelete={deleteLocker}
                onSaveLocker={saveLocker}
                isPositionFree={isPositionFree}
                viewMode={viewMode}
                editMode={editMode}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Control buttons */}
      <div style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        display: 'flex',
        gap: '12px',
        flexDirection: 'column'
      }}>
        {/* Create mode buttons */}
        {!viewMode && !editMode && (
          <>
            <button 
              onClick={resetView}
              style={{
                padding: '12px 20px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'background-color 0.2s ease',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#0056b3'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#007bff'}
            >
              {t("lockers.resetView")}
            </button>
            
            <button 
              onClick={saveArrangement}
              disabled={isLoading}
              style={{
                padding: '12px 20px',
                backgroundColor: isLoading ? '#6c757d' : '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'background-color 0.2s ease',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
              onMouseEnter={(e) => {
                if (!isLoading) e.target.style.backgroundColor = '#1e7e34';
              }}
              onMouseLeave={(e) => {
                if (!isLoading) e.target.style.backgroundColor = '#28a745';
              }}
            >
              {isLoading ? t("lockers.saving") : t("lockers.saveArrangment")}
            </button>
          </>
        )}

        {/* View mode buttons */}
        {viewMode && (
          <button 
            onClick={resetView}
            style={{
              padding: '12px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'background-color 0.2s ease',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#0056b3'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#007bff'}
          >
            {t("lockers.resetView")}
          </button>
        )}

        {/* Edit mode buttons */}
        {editMode && (
          <>
            <button 
              onClick={resetView}
              style={{
                padding: '12px 20px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'background-color 0.2s ease',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#0056b3'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#007bff'}
            >
              {t("lockers.resetView")}
            </button>

            <button 
              onClick={saveGroup}
              disabled={isGroupOperationLoading}
              style={{
                padding: '12px 20px',
                backgroundColor: isGroupOperationLoading ? '#6c757d' : '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: isGroupOperationLoading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'background-color 0.2s ease',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
              onMouseEnter={(e) => {
                if (!isGroupOperationLoading) e.target.style.backgroundColor = '#1e7e34';
              }}
              onMouseLeave={(e) => {
                if (!isGroupOperationLoading) e.target.style.backgroundColor = '#28a745';
              }}
            >
              {isGroupOperationLoading ? t("lockers.saving") : t("lockers.saveGroup")}
            </button>

            <button 
              onClick={deleteGroup}
              disabled={isGroupOperationLoading}
              style={{
                padding: '12px 20px',
                backgroundColor: isGroupOperationLoading ? '#6c757d' : '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: isGroupOperationLoading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'background-color 0.2s ease',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
              onMouseEnter={(e) => {
                if (!isGroupOperationLoading) e.target.style.backgroundColor = '#c82333';
              }}
              onMouseLeave={(e) => {
                if (!isGroupOperationLoading) e.target.style.backgroundColor = '#dc3545';
              }}
            >
              {isGroupOperationLoading ? t("lockers.deleting") : t("lockers.deleteGroup")}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default LockerArrangement;